import { Col, Icon, Row, Spinner2, Switch, Text } from 'components';
import { Api, QUIZ_TYPE, theme } from 'lib';
import { format } from 'date-fns';
import React, { useState } from 'react';
import styled from 'styled-components';
import { getGameLabel } from './GamePicker';

const Chip = styled.span`
  display: inline-block;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-family: CeraPro;
  font-weight: 600;
  letter-spacing: 0.3px;
  background: ${(props) =>
    props.tone === 'daily' ? `${theme.GREEN}20` : `${theme.PURPLE || theme.PRIMARY}20`};
  color: ${(props) => (props.tone === 'daily' ? theme.GREEN : theme.PURPLE || theme.PRIMARY)};
`;

// Icon-only button. The clickable area matches the visible icon (no extra padding).
const IconBtn = styled.button`
  background: none;
  border: none;
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border-radius: 6px;
  padding: 0;
  color: ${(props) => (props.danger ? theme.RED : theme.GREY_60)};
  transition: background 0.12s ease;
  &:hover {
    background: ${theme.GREY_10};
  }
`;

// 12-hour clock throughout — admins read these at a glance and 24-hour reads as a code.
const DATE_TIME = 'MMM dd, h:mm a';

const plural = (n, one, many) => `${n.toLocaleString()} ${n === 1 ? one : many}`;

// When the quiz runs. Half-time quizzes have no fixed window — they fire off the match's
// HT event — so they show the match instead of a start/end pair.
const formatWindow = (q) => {
  if (q.quizType === QUIZ_TYPE.HALF_TIME) {
    const label =
      q.game && typeof q.game === 'object' ? getGameLabel(q.game, q.team?._id || q.team) : '';
    return label ? `At half time · ${label}` : 'At half time';
  }
  if (q.startDate && q.endDate) {
    return `${format(new Date(q.startDate), DATE_TIME)} → ${format(
      new Date(q.endDate),
      DATE_TIME
    )}`;
  }
  return 'No schedule set';
};

const QuizListTable = ({
  quizzes,
  loading,
  isP4P,
  // Rows come from more than one channel under "All channels", so name the owner.
  showChannel,
  onEdit,
  onViewResponses,
  onDelete,
  onToggleActive
}) => {
  const [togglingId, setTogglingId] = useState(null);

  const handleToggle = async (q, e) => {
    e?.stopPropagation();
    setTogglingId(q._id);
    const res = await Api.put(
      '/admin/quizzes/update',
      { id: q._id, isActive: !q.isActive },
      null,
      { silent: true }
    );
    setTogglingId(null);
    if (res?.data || res?.msg) {
      onToggleActive(res.data || { ...q, isActive: !q.isActive });
    }
  };

  if (loading) {
    return (
      <Row centerAll pad="40px 0">
        <Spinner2 />
      </Row>
    );
  }

  if (!quizzes.length) {
    return (
      <Row centerAll pad="40px 0">
        <Text col={theme.GREY_60}>No quizzes match the current filters.</Text>
      </Row>
    );
  }

  return (
    <Col wid="100%" gap="8px">
      {quizzes.map((q) => {
        // Team admins can't edit/delete/toggle a quiz a P4P admin created (mirrors backend).
        const manageable = isP4P || q.createdByRole !== 'p4p';
        const answers = q.totalSubmissions || 0;
        const correct = q.correctSubmissions || 0;
        return (
        <Row
          key={q._id}
          between
          pad="14px 18px"
          style={{
            background: 'white',
            borderRadius: 10,
            border: `1px solid ${theme.GREY_20}`,
            alignItems: 'center'
          }}
        >
          {/* LEFT: title on top, then each meta item on its own line below */}
          <Col flex={1} style={{ minWidth: 0, alignItems: 'flex-start', alignSelf: 'flex-start' }}>
            <Text
              med
              style={{
                minWidth: 0,
                width: '100%',
                overflowWrap: 'anywhere'
              }}
            >
              {q.question}
            </Text>
            {showChannel && q.team && typeof q.team === 'object' && (
              <Text c2 col={theme.PRIMARY} marg="4px 0 0">
                {q.team.name || q.team.username}
              </Text>
            )}

            {/* When it runs */}
            <Text c2 col={theme.GREY_60} marg="6px 0 0">
              {formatWindow(q)}
            </Text>

            {/* How it performed — emphasized once anyone has answered, so a live quiz
                stands out from an untouched one at a glance. */}
            <Text
              c2
              med={answers > 0}
              col={answers > 0 ? theme.GREY_80 : theme.GREY_40}
              marg="4px 0 0"
            >
              {plural(answers, 'answer', 'answers')}
              {answers > 0
                ? ` · ${correct.toLocaleString()} correct (${Math.round((correct / answers) * 100)}%)`
                : ' · 0 correct'}
            </Text>

            {/* Configuration, deliberately quietest */}
            <Text c2 col={theme.GREY_40} marg="4px 0 0">
              {plural(q.rewardPoints || 0, 'pt', 'pts')}
              {q.createdAt ? ` · created ${format(new Date(q.createdAt), 'MMM dd, yyyy')}` : ''}
            </Text>
          </Col>

          {/* RIGHT: type chip + status toggle + actions — vertically centered, symmetric edge spacing via the row's own padding */}
          <Row noFlex center gap="12px" marg="0 0 0 16px" style={{ alignSelf: 'center' }}>
            <Chip tone={q.quizType === QUIZ_TYPE.DAILY ? 'daily' : 'halftime'}>
              {q.quizType === QUIZ_TYPE.DAILY ? 'Daily' : 'Half-time'}
            </Chip>

            <Row noFlex center>
              <Text c2 col={q.isActive ? theme.GREEN : theme.GREY_60} marg="0 6px 0 0">
                {q.isActive ? 'Active' : 'Inactive'}
              </Text>
              {manageable && (
                <Switch
                  activated={q.isActive}
                  onClick={(e) => handleToggle(q, e)}
                  disabled={togglingId === q._id}
                />
              )}
            </Row>

            <IconBtn onClick={() => onViewResponses(q)} title="View participants" type="button">
              <Icon type="people" col="currentColor" size={16} />
            </IconBtn>

            {manageable ? (
              <>
                <IconBtn onClick={() => onEdit(q)} title="Edit" type="button">
                  <Icon type="edit" col="currentColor" size={16} />
                </IconBtn>

                <IconBtn onClick={() => onDelete(q)} title="Delete" type="button" danger>
                  <Icon type="delete" col="currentColor" size={16} />
                </IconBtn>
              </>
            ) : (
              <Text c2 col={theme.GREY_40} title="Created by a P4P admin">
                Managed by P4P
              </Text>
            )}
          </Row>
        </Row>
        );
      })}
    </Col>
  );
};

export default QuizListTable;
